import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { useGetCreateJobStatus } from "@/lib/api/dorman";
import { MimeType } from "@/lib/api/utils";
import { UploadPresignedFile } from "@/lib/file";
import { UploadIcon } from "lucide-react";
import { useRef, useEffect, useState } from "react";
import {
  UploadReportValidation,
  useGenerateCfrSuggestionsReportUpload,
  useListAgtechReleasePresets,
  useGenerateAgtechReleaseBreakdown,
  AgtechReleaseReportRequestValidation,
  AgtechReleasePreset,
  type AgtechReleaseReportRequest,
} from "@/lib/api/cfr";
import { useQueryClient } from "@tanstack/react-query";
import { AgtechReleaseRangeBuilder, newAgtechReleaseRange } from "./AgtechReleaseRangeBuilder";

type ReportType = "direct_ship" | "agtech_release_report";

export function UploadSuggestionsReportDialog() {
  const { toast } = useToast();
  const [link, setLink] = useState<string | undefined | null>();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [reportType, setReportType] = useState<ReportType>("direct_ship");
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const [agtechRanges, setAgtechRanges] = useState<AgtechReleaseReportRequest["ranges"]>(() => [newAgtechReleaseRange()]);
  const jobStatusRes = useGetCreateJobStatus(jobId);
  const createUploadJob = useGenerateCfrSuggestionsReportUpload();
  const presetsResult = useListAgtechReleasePresets();
  const generateMutation = useGenerateAgtechReleaseBreakdown();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const presets: AgtechReleasePreset[] = presetsResult.data?.success ? presetsResult.data.data : [];

  const onGenerateReport = async (e: React.FormEvent<HTMLFormElement>) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    try {
      if (!fileToUpload) { return; }
      if (isGenerating) { return; }
      setIsGenerating(true);

      const validationData = {
        filename: fileToUpload?.name || "",
        mime_type: fileToUpload?.type || "",
        report_type: "direct_ship" as const,
      };

      const parseDataResult = UploadReportValidation.safeParse(validationData);

      if (!parseDataResult.success) {
        toast({
          title: "Invalid File"
        })
        return;
      }

      const result = await createUploadJob(parseDataResult.data);
      if (!result.success) {
        throw result;
      }

      const resultData = result.data;
      setJobId(resultData.job_id);

      const uploadResult = await UploadPresignedFile(resultData.link, fileToUpload);
      if (!result.success) {
        console.error("Failed to upload report", uploadResult);
        toast({
          title: "Failed to upload report"
        })
        return;
      }

      toast({
        title: "Processing now.",
        description: "",
      });
      setIsOpen(false);
    } catch (error) {
      setLink(null);
      console.error("Failed to generate cfr suggestions upload report", error);

      toast({
        title: "Failed to generate report",
        description: "",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRunReport = async () => {
    if (generateMutation.isLoading) return;

    const parsed = AgtechReleaseReportRequestValidation.safeParse({ ranges: agtechRanges });
    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      toast({
        title: "Invalid report ranges",
        description: firstIssue ? firstIssue.message : "Please correct the selected ranges.",
        variant: "destructive",
      });
      return;
    }

    const result = await generateMutation.mutateAsync(parsed.data);
    if (!result.success) {
      toast({
        title: "Failed to generate report",
        description: result.message || "Please try again.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Report generated.",
      description: "",
    });
    setAgtechRanges([newAgtechReleaseRange()]);
    setIsOpen(false);
  };

  const jobStatus = jobStatusRes?.data?.data?.job_status;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(status) => {
        if (!status) {
          setLink(null);
          setAgtechRanges([newAgtechReleaseRange()]);
        }
        if (status) {
          setJobId(undefined);
        }
        setIsOpen(status);
      }}
    >
      <DialogTrigger>
        <span className="sr-only">Upload Cash Sales Report</span>
        <Button className="gap-2" asChild>
          <span>
            {!jobId && (
              <>
                <UploadIcon className="h-4 w-4" />
                Upload Report
              </>
            )}
            {jobId && <JobStatus jobStatus={jobStatus} setJobId={setJobId} />}
          </span>
        </Button>
      </DialogTrigger>
      <DialogContent className="flex flex-col lg:max-w-[70vw] max-h-[90vh] p-8 justify-center">
        <DialogHeader>
          <DialogTitle>
            Upload CFR Suggestions Report
          </DialogTitle>
          <DialogDescription>
            Select the report type and upload the corresponding file
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={reportType}
          onValueChange={(value) => {
            setReportType(value as ReportType);
            setFileToUpload(null);
            setAgtechRanges([newAgtechReleaseRange()]);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="direct_ship">Direct Ship</TabsTrigger>
            <TabsTrigger value="agtech_release_report">Agtech Release Report</TabsTrigger>
          </TabsList>

          <TabsContent value="direct_ship">
            <form className="space-y-6" onSubmit={onGenerateReport}>
              <div className="space-y-2 grid">
                <Label htmlFor="directShipFileInput">Direct Ship Report (Excel)</Label>
                <Input
                  id="directShipFileInput"
                  type="file"
                  ref={fileInputRef}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFileToUpload(event.target.files?.[0] || null)}
                  accept=".xlsx"
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsOpen(false)}
                  disabled={isGenerating}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isGenerating}>
                  {isGenerating ? "Uploading..." : "Upload"}
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="agtech_release_report">
            <div className="space-y-4">
              <AgtechReleaseRangeBuilder
                key={`${isOpen}-${reportType}`}
                presets={presets}
                onChange={setAgtechRanges}
              />

              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsOpen(false)}
                  disabled={generateMutation.isLoading}
                >
                  Cancel
                </Button>
                <Button type="button" onClick={handleRunReport} disabled={generateMutation.isLoading}>
                  {generateMutation.isLoading ? "Generating..." : "Run report"}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function JobStatus({ jobStatus, setJobId }: { jobStatus: string; setJobId: any }) {
  const className = "text-xs font-bold tracking-tight";
  const queryClient = useQueryClient();

  useEffect(() => {
    if (jobStatus === "completed") {
      setJobId(undefined);
      queryClient.invalidateQueries({ queryKey: ["cfr", "suggestions"] });
    }
  }, [jobStatus, queryClient])

  if (!jobStatus) { return null }
  if (jobStatus === "queued") { return (<div className={className}>Processing</div>) }
  if (jobStatus === "doesnt_exist") { return (<div className={className}>Failed</div>) }
  if (jobStatus === "failed") { return (<div className={className}>Failed</div>) }
  if (jobStatus === "halt") { return (<div className={className}>Failed</div>) }
  if (jobStatus !== "completed") { return (<div className={className}>Unknown</div>); }

  return null;
}
